<template>
  <!-- 客户到期预警（数据来源：合同明细 crm_contract_item，按明细到期时间计算） -->
  <div class="subscription-list">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true">
        <template #default>
          <customer-select v-model="searchInfo.customerId" placeholder="请选择客户">
            <template #prefix>客户</template>
          </customer-select>
          <el-input v-model="searchInfo.searchKey" placeholder="合同编号/合同名称" clearable @keydown.enter="resetSearch">
            <template #prefix>关键字</template>
          </el-input>
          <el-select v-model="searchInfo.sort" placeholder="排序">
            <template #prefix>排序</template>
            <el-option label="按到期升序" value="expireAsc"/>
            <el-option label="按到期降序" value="expireDesc"/>
            <el-option label="按建立时间" value="createdDesc"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button el-type="primary" class="plain-status" :plain="searchInfo.plain !== 'primary'" @click="resetSearchPlain('primary')">全部</page-button>
          <page-button el-type="info" class="plain-status" :plain="searchInfo.plain !== 'expired'" @click="resetSearchPlain('expired')">已过期</page-button>
          <page-button el-type="danger" class="plain-status" :plain="searchInfo.plain !== 'urgent'" @click="resetSearchPlain('urgent')">紧急(7天)</page-button>
          <page-button el-type="warning" class="plain-status" :plain="searchInfo.plain !== 'warn'" @click="resetSearchPlain('warn')">预警(30天)</page-button>
          <page-button el-type="success" class="plain-status" :plain="searchInfo.plain !== 'success'" @click="resetSearchPlain('success')">正常</page-button>
        </template>
      </search-condition>
      <list-table>
        <yq-table :data="dataList" height="100%" stripe border page-name="subscriptionList">
          <yq-table-column label="合同编号" prop="contractNumber" min-width="170rem">
            <template v-slot="{row}">
              <a @click="openContract(row)">{{ row.contractNumber || '—' }}</a>
            </template>
          </yq-table-column>
          <yq-table-column label="客户" prop="customerName" min-width="240rem">
            <template v-slot="{row}">
              <strong>{{ row.customerName || '—' }}</strong>
            </template>
          </yq-table-column>
          <yq-table-column label="产品" prop="productName" min-width="180rem" :show-overflow-tooltip="true">
            <template v-slot="{row}">{{ row.productName || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="产品线" prop="productLineName" min-width="120rem">
            <template v-slot="{row}">{{ row.productLineName || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="版本" prop="version" min-width="120rem">
            <template v-slot="{row}">{{ row.version || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="用户数" prop="userCount" width="90rem" align="center">
            <template v-slot="{row}">
              <span v-if="row.userCount">{{ row.userCount }}</span>
              <span v-else class="muted">—</span>
            </template>
          </yq-table-column>
          <yq-table-column label="开始" prop="startDate" min-width="120rem">
            <template v-slot="{row}">{{ dateOnly(row.startDate) }}</template>
          </yq-table-column>
          <yq-table-column label="到期时间" prop="expireDate" min-width="120rem">
            <template v-slot="{row}">
              <strong>{{ dateOnly(row.expireDate) }}</strong>
            </template>
          </yq-table-column>
          <!-- 剩余/状态按明细到期时间计算，与后端预警档同一口径 -->
          <yq-table-column label="剩余" prop="remain" min-width="110rem" align="center">
            <template v-slot="{row}">
              <span v-if="row.expireDate" :style="{color: remainColor(row.expireDate)}">{{ remainText(row.expireDate) }}</span>
              <span v-else class="muted">—</span>
            </template>
          </yq-table-column>
          <yq-table-column label="状态" prop="statusText" width="110rem" align="center">
            <template v-slot="{row}">
              <el-tag v-if="row.expireDate" size="small" :type="statusTag(row.expireDate)">{{ statusOf(row.expireDate) }}</el-tag>
              <span v-else class="muted">—</span>
            </template>
          </yq-table-column>
          <yq-table-column label="销售" prop="salesName" min-width="110rem">
            <template v-slot="{row}">{{ row.salesName || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="操作" prop="options" align="center" fixed="right" width="120rem" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <table-button @click="openContract(row)">查看合同</table-button>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList">
        <template #default>
          <div v-if="loadStatsDone" class="sub-banner">
            <span class="sub-banner-title">⚠ 到期预警</span>
            <span class="sub-stat" :class="statClass('all')" @click="quickFilter('')">全部 <b>{{ expireStats.total }}</b></span>
            <span v-if="expireStats.expired" class="sub-stat sub-stat-danger" @click="quickFilter('已过期')">已过期 <b>{{ expireStats.expired }}</b></span>
            <span v-if="expireStats.urgent" class="sub-stat sub-stat-warn" @click="quickFilter('紧急')">7天内紧急 <b>{{ expireStats.urgent }}</b></span>
            <span v-if="expireStats.warn" class="sub-stat sub-stat-info" @click="quickFilter('预警')">30天内预警 <b>{{ expireStats.warn }}</b></span>
            <span v-if="expireStats.normal" class="sub-stat sub-stat-ok" @click="quickFilter('正常')">正常 <b>{{ expireStats.normal }}</b></span>
          </div>
        </template>
      </form-page>
    </list-page>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import CustomerSelect from "@/components/h5/partner/CustomerSelect.vue";
import {useRouter} from "vue-router";
import Util from "@/util/util";
import contractApi from "@/api/dz-crm/partner/contractApi";

export default {
  setup() {
    return {router: useRouter()};
  },
  name: "SubscriptionList",
  components: {
    CustomerSelect, YqTableColumn, TableButton, PageButton,
    SearchCondition, FormPage, ListTable, ListPage,
  },
  data() {
    return {
      searchInfo: {pageNumber: 1, searchKey: "", status: "", sort: "expireAsc", plain: "primary"},
      dataList: [],
      expireStats: {total: 0, expired: 0, urgent: 0, warn: 0, normal: 0},
      loadStatsDone: false,
    };
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    this.loadExpireStats();
    this.resetSearch();
  },
  methods: {
    async loadExpireStats() {
      const stats = await contractApi.expireStats();
      this.expireStats = stats || {total: 0, expired: 0, urgent: 0, warn: 0, normal: 0};
      this.loadStatsDone = true;
    },
    async queryList() {
      const res = await contractApi.expireList(this.searchInfo);
      this.dataList = res.records || [];
      this.searchInfo.totalCount = res.totalCount;
    },
    resetSearchPlain(ex) {
      this.searchInfo.plain = ex;
      if (ex === "primary") {
        this.searchInfo.status = "";
      } else if (ex === "expired") {
        this.searchInfo.status = "已过期";
      } else if (ex === "urgent") {
        this.searchInfo.status = "紧急";
      } else if (ex === "warn") {
        this.searchInfo.status = "预警";
      } else if (ex === "success") {
        this.searchInfo.status = "正常";
      }
      this.resetSearch();
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    quickFilter(status) {
      this.searchInfo.status = status;
      this.resetSearch();
    },
    statClass() {
      return {};
    },
    openContract(row) {
      this.router.push(`/contract/list/detail?id=${row.contractId}`);
    },
    // ---------- 到期状态计算（与后端预警档一致） ----------
    daysToExpire(dateStr) {
      if (!dateStr) return null;
      const s = String(dateStr).slice(0, 10).split("-");
      if (s.length !== 3) return null;
      const today = new Date();
      const exp = new Date(Number(s[0]), Number(s[1]) - 1, Number(s[2]));
      return Math.round((exp - new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000);
    },
    statusOf(dateStr) {
      const d = this.daysToExpire(dateStr);
      if (d == null) return "正常";
      if (d < 0) return "已过期";
      if (d <= 7) return "紧急";
      if (d <= 30) return "预警";
      return "正常";
    },
    statusTag(dateStr) {
      const map = {"已过期": "danger", "紧急": "warning", "预警": "primary", "正常": "success"};
      return map[this.statusOf(dateStr)] || "info";
    },
    remainText(dateStr) {
      const d = this.daysToExpire(dateStr);
      if (d == null) return "—";
      if (d < 0) return "已过 " + (-d) + " 天";
      if (d === 0) return "今天到期";
      return "还有 " + d + " 天";
    },
    remainColor(dateStr) {
      const d = this.daysToExpire(dateStr);
      if (d < 0) return "#c0392b";
      if (d <= 7) return "#e67e22";
      if (d <= 30) return "#2980b9";
      return "#2e8b57";
    },
    dateOnly(dateStr) {
      return dateStr ? String(dateStr).slice(0, 10) : "—";
    },
  },
};
</script>

<style lang="scss" scoped>
.plain-status {
  :deep(.el-button) {
    border-radius: 30px;
  }
}

.subscription-list {
  height: 100%;
  .sub-banner {
    .sub-banner-title {
      font-weight: 700;
      margin-right: 18px;
      color: #d48806;
    }
    .sub-stat {
      display: inline-flex;
      align-items: center;
      padding: 3px 10px;
      margin-right: 8px;
      border-radius: 12px;
      background: #f5f7fa;
      cursor: pointer;
      user-select: none;
      &.active {
        outline: 2px solid var(--el-color-primary);
      }
      &.sub-stat-danger {
        color: #c0392b;
        background: #fdecea;
      }
      &.sub-stat-warn {
        color: #e67e22;
        background: #fef3e7;
      }
      &.sub-stat-info {
        color: #2980b9;
        background: #eaf3fb;
      }
      &.sub-stat-ok {
        color: #2e8b57;
        background: #eaf6ef;
      }
      b {
        margin-left: 4px;
        font-size: 15px;
      }
    }
  }
}
</style>
